<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-head">
        <div class="auth-title">注册</div>
        <div class="auth-subtitle">欢迎加入，填写信息完成注册</div>
      </div>

      <form class="auth-form" @submit.prevent="submit">
        <label class="field">
          <span class="field-label">邮箱</span>
          <input v-model="form.email" class="field-input" type="email" placeholder="邮箱" autocomplete="email" />
        </label>

        <label class="field">
          <span class="field-label">密码</span>
          <input v-model="form.password" class="field-input" type="password" placeholder="密码（至少6位）"
            autocomplete="new-password" />
        </label>

        <label class="field">
          <span class="field-label">确认密码</span>
          <input v-model="form.confirm" class="field-input" type="password" placeholder="确认密码"
            autocomplete="new-password" />
        </label>

        <label class="field">
          <span class="field-label">邮箱验证码</span>
          <div class="code-row">
            <input v-model="form.code" class="field-input" type="text" placeholder="邮箱验证码"
              autocomplete="one-time-code" />
            <button class="code-btn" type="button" :disabled="countdown > 0 || sending" @click="sendCode">
              {{ countdown > 0 ? `${countdown}s` : "获取验证码" }}
            </button>
          </div>
        </label>
        <div style="text-align:center;">
          <button class="pink-btn" type="submit" :disabled="loading">
            {{ loading ? "提交中..." : "完成注册" }}
          </button>

        </div>


        <div class="extra-tip">
          已有账号？ <a href="/users/login">去登录</a>
        </div>
      </form>
    </div>

    <AppFooter active="me" />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import AppFooter from "~/components/AppFooter.vue";

const loading = ref(false);
const sending = ref(false);
const countdown = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

const form = reactive({
  email: "",
  password: "",
  confirm: "",
  code: "",
});

const validateForm = () => {
  if (!form.email.trim()) {
    message.error("请输入邮箱");
    return false;
  }
  if (!form.password.trim()) {
    message.error("请输入密码");
    return false;
  }
  if (form.password.trim().length < 6) {
    message.error("密码至少6位");
    return false;
  }
  if (!form.confirm.trim()) {
    message.error("请确认密码");
    return false;
  }
  if (form.confirm.trim() != form.password.trim()) {
    message.error("两次密码不一致");
    return false;
  }
  if (!form.code.trim()) {
    message.error("请输入验证码");
    return false;
  }
  return true;
};

function submit() {
  if (!validateForm()) return;
  onFinish();
}

function startCountdown() {
  countdown.value = 60;
  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0 && timer) {
      countdown.value = 0;
      clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

async function sendCode() {
  if (countdown.value > 0 || sending.value) return;
  if (!form.email.trim()) {
    message.warning("请先输入邮箱");
    return;
  }

  try {
    sending.value = true;
    const data = await $api_UserGetEmailCheckCode({
      method: "POST",
      body: { email: form.email },
      server: false,
    });

    const curData = data as IApiResponse<any>;
    if (curData?.code === 1) {
      message.success("验证码已发送，请查收");
      startCountdown();
    } else {
      message.error(curData?.message || "获取失败");
    }
  } catch (error: any) {
    console.error("code failed:", error);
    message.error(error?.message || "网络错误，请稍后重试");
  } finally {
    sending.value = false;
  }
}

async function onFinish() {
  loading.value = true;
  try {
    const channelCode = import.meta.client
      ? window.localStorage.getItem("channelCode")
      : null;
    const inviterId = import.meta.client
      ? window.localStorage.getItem("inviterId")
      : null;
    const extraParams: Record<string, string> = {};
    if (channelCode) {
      extraParams.channelCode = channelCode;
    }
    if (inviterId) {
      extraParams.inviterId = inviterId;
    }
    const { data } = await api_UserRegister({
      method: "POST",
      body: {
        email: form.email,
        password1: form.password,
        password2: form.confirm,
        code: form.code,
        ...extraParams,
      },
      server: false,
      key: "api_UserRegister" + new Date().getMilliseconds(),
    });
    const curData = data.value as IApiResponse<any>;
    if (curData?.code === 1) {
      message.success("注册成功");
      setTimeout(() => {
        navigateTo("/users/login");
      }, 2000);
    } else {
      message.error(curData?.message || "注册失败");
    }
  } catch (e) {
    message.error("注册失败，请稍后重试");
  } finally {
    loading.value = false;
  }
}

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
:global(body) {
  background: #f5f7fa;
  font-family: "PingFang SC", "Helvetica Neue", Arial, sans-serif;
}

.auth-page {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffeaf1 0%, #fff5f9 100%);
  padding: 24px 16px;
}

.auth-card {
  width: 100%;
  max-width: 420px;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(251, 114, 153, 0.16);
  border: 1px solid #ffe0eb;
  background: #fff;
}

.auth-head {
  text-align: center;
  margin-bottom: 12px;
}

.auth-title {
  font-size: 24px;
  font-weight: 700;
  color: #fb7299;
}

.auth-subtitle {
  margin-top: 4px;
  font-size: 14px;
  color: #a06b80;
}

.auth-form {
  margin-top: 12px;
}

.field {
  display: grid;
  gap: 6px;
  margin: 12px;
}

.field-label {
  font-size: 12px;
  color: #8c9196;
}

.field-input {
  width: 100%;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid #f3d7e1;
  background: #fff;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: #fb7299;
  box-shadow: 0 0 0 2px rgba(251, 114, 153, 0.2);
}

.code-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 8px;
}

.code-btn {
  border: 1px solid #fb7299;
  background: #fff;
  color: #fb7299;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.code-btn:disabled {
  border-color: #e5e7eb;
  color: #a1a1a1;
  cursor: not-allowed;
}

.pink-btn {
  --pink: #fb7299;
  width: 90%;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--pink);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(251, 114, 153, 0.35);
}

.pink-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.extra-tip {
  margin-top: 10px;
  font-size: 13px;
  color: #8c9196;
  text-align: center;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 4px 0;
    box-shadow: none;
    background: transparent;
  }
}
</style>
